
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  ICountryConfig,
  IDeclaredValueFingerprint,
  IDeclaredValuePackFingerprint,
  IPickUpService,
  IRequestedAmount,
  ISolCustomerCommodity,
  ISolCustomerPack,
  IUserClientSubclientServicepointData,
  IUserDetailData,
  IAmountByCurrency,
} from "@/entities";
import DetailConsignment from "@/components/orders/DetailConsignment.vue";
import AgilePickupDeclaredValueTable from "@/components/orders/agile_pickup/AgilePickupDeclaredValueTable.vue";
import AgilePickupDeclaredValuePackTable from "@/components/orders/agile_pickup/AgilePickupDeclaredValuePackTable.vue";
import { Getter } from "vuex-class";
import QrModal from "@/components/orders/agile_pickup/QRModal.vue";
import IconQr from "@/components/common/icon/IconQr.vue";

@Component({
  name: "detail-pickup",
  components: {
    IconQr,
    QrModal,
    AgilePickupDeclaredValueTable,
    AgilePickupDeclaredValuePackTable,
    DetailConsignment,
  },
})
export default class DetailPickup extends Vue {
  @Prop({ required: true })
  pickUpService!: IPickUpService | undefined;
  @Prop({ required: true })
  serviceCode!: string;
  @Prop({ required: true })
  serviceStatusCode!: string;
  @Prop({ required: false, type: Number })
  woStatusCode?: number;
  @Prop({ required: false, default: false })
  agilePickup!: boolean;
  @Prop({ required: true })
  packs!: ISolCustomerPack[];
  @Prop({ required: false, default: null })
  servicepointSelected!: IUserClientSubclientServicepointData | null;

  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;

  public loading = true;
  public declaredValues: IDeclaredValueFingerprint | null = null;
  public declaredValuesString: string | null = null;
  public declaredValuesPack: IDeclaredValuePackFingerprint | null = null;
  public declaredValuesPackString: string | null = null;
  private PACK_CODE = "28";

  get isAgilePickupAvailable(): boolean {
    const hasRequestAmounts =
      this.pickUpService?.requestedAmounts?.some((amount) => (amount.totalAmount ?? 0) > 0) ||
      false;
    const hasDeclaredValues =
      !!this.declaredValues?.dt?.dr?.length || !!this.declaredValuesPack?.dt?.dr?.length || false;
    return !hasRequestAmounts || hasDeclaredValues;
  }

  sumRequestedAmounts(
    values: IRequestedAmount[] | undefined
  ): { amount: number; currencyIsoCode: string; currencySymbol: string }[] {
    const amountsByCurrency: { amount: number; currencyIsoCode: string; currencySymbol: string }[] =
      [];

    values?.forEach((value) => {
      const amountByCurrency = amountsByCurrency.find(
        (amountByCurrency) => amountByCurrency.currencyIsoCode === value.currencyIsoCode
      );

      if (amountByCurrency) {
        amountByCurrency.amount = (amountByCurrency.amount ?? 0) + (value.totalAmount ?? 0);
      } else {
        amountsByCurrency.push({
          amount: value.totalAmount ?? 0,
          currencyIsoCode: value.currencyIsoCode ?? "",
          currencySymbol: value.currencySymbol ?? "",
        });
      }
    });
    return amountsByCurrency;
  }

  get requestedAmountByCurrency(): IAmountByCurrency[] {
    return this.sumRequestedAmounts(this.pickUpService?.requestedAmounts).map((amount) => {
      const sourceAmount = this.pickUpService?.requestedAmounts?.find(
        (value) => value.currencyIsoCode === amount.currencyIsoCode
      );
      return {
        ...amount,
        currencyId: sourceAmount?.currencyId,
      };
    });
  }

  get requestedAmountsWithCommodity(): IRequestedAmount[] {
    const requestedAmounts =
      this.pickUpService?.requestedAmounts?.filter((amount) => {
        const hasCommodity = !!amount.commodityDescription || !!amount.commodityCode;
        const hasDenominations = !!amount.denominations?.length;
        const hasPacks = !!amount.packs?.length;
        const hasTotalAmount = amount.totalAmount != null;
        return hasCommodity && !hasDenominations && !hasPacks && hasTotalAmount;
      }) || [];

    const groupedAmounts = new Map<string, IRequestedAmount>();

    requestedAmounts.forEach((amount) => {
      const key = `${amount.commodityCode || amount.commodityDescription || "-"}|${
        amount.currencyIsoCode || ""
      }`;
      const existing = groupedAmounts.get(key);

      if (existing) {
        existing.totalAmount = (existing.totalAmount ?? 0) + (amount.totalAmount ?? 0);
        existing.commodityDescription =
          existing.commodityDescription || amount.commodityDescription;
      } else {
        groupedAmounts.set(key, {
          ...amount,
          totalAmount: amount.totalAmount ?? 0,
        });
      }
    });

    return Array.from(groupedAmounts.values());
  }

  get commodities(): ISolCustomerCommodity[] {
    return this.pickUpService?.agreementCommodities?.filter((c) => c.code !== this.PACK_CODE) || [];
  }

  get hasPack(): boolean {
    return !!this.pickUpService?.agreementCommodities?.find((c) => c.code === this.PACK_CODE);
  }

  get qrValues(): string[] {
    const values: string[] = [];
    if (this.declaredValues?.fp) {
      values.push(this.declaredValuesString!);
    }
    if (this.declaredValuesPack?.fp) {
      values.push(this.declaredValuesPackString!);
    }
    return values;
  }

  async mounted(): Promise<void> {
    if (this.agilePickup) {
      await this.getDeclaredValues();
      return;
    }

    this.loading = false;
  }

  async onRefreshAgilePickup(): Promise<void> {
    this.loading = true;
    await new Promise<void>((resolve) => this.$emit("refresh", resolve));
    await this.getDeclaredValues();
  }

  async getDeclaredValues(): Promise<void> {
    const params = {
      countryCode: this.getLoggedUser.country!.code!,
      serviceCode: this.serviceCode,
    };

    this.loading = true;

    const res = await this.$services.solServiceApi
      .getDeclaredValues(params)
      .finally(() => (this.loading = false));

    this.declaredValues = res?.declaredValues || null;
    this.declaredValuesString = res ? JSON.stringify(res.declaredValues) : null;
    this.declaredValuesPack = res?.declaredValuesPack || null;
    this.declaredValuesPackString = res ? JSON.stringify(res.declaredValuesPack) : null;
  }
}
